import React from 'react';
import PropTypes from 'prop-types';
import {GlossaryLogo} from './GlossaryLogo';
import {SearchBox} from './SearchBox';
import {SearchInput} from './SearchInput';
import {SearchIcon} from './SearchIcon';
import {CategoryTabs} from './CategoryTabs';
import {GlossaryListing} from './GlossaryListing';
import {GlossaryListBlock} from './GlossaryListBlock';
import {DetailsBlock} from './DetailsBlock';
import {GlossaryDetails} from './GlossayDetails';
import {CloseButton} from '@learningblocks/storybook-components';
import {useGlossaryHook} from './Glossary.container';
import style from './style.module.scss';

/**
 * MainComponent:
 *  - Component puts together all child components in Glossary
 *    and provider their props and state
 */
function MainComponent(props) {
  const {state, onFetchGlossary, onFetchGlossaryItem} = React.useContext(
    GlossaryDispatch
  );

  const onSearch = (event) => {
    event.preventDefault();
    onFetchGlossary();
  };

  const isGlossaryEmpty = React.useMemo(() => {
    return state.glossaryList.length <= 0;
  }, [state.glossaryList.length]);

  return (
    <div className={style.glossary}>
      <div className={style.glossary_left}>
        <div className={style.glossary_left_header}>
          <GlossaryLogo />
          <SearchBox>
            <SearchInput value={state.searchText} onEnterKey={onSearch} />
            <SearchIcon onClick={onSearch} />
          </SearchBox>
        </div>
        <div className={style.glossary_left_middle}>
          <CategoryTabs categories={state.categories} />
        </div>
        <div className={style.glossary_left_body}>
          <GlossaryListBlock
            {...state.listApi}
            onRetry={onSearch}
            isEmpty={isGlossaryEmpty}>
            <GlossaryListing glossaryList={state.glossaryList} />
          </GlossaryListBlock>
        </div>
      </div>
      <div className={style.glossary_right}>
        <CloseButton
          close={props.closeGlossary}
          className={style.glossary_right_closeButton}
        />
        <DetailsBlock {...state.contentApi} onRetry={onFetchGlossaryItem}>
          <GlossaryDetails
            close={props.closeGlossary}
            {...state.glossaryItem}
          />
        </DetailsBlock>
      </div>
    </div>
  );
}

MainComponent.propTypes = {
  /**
   * function involked to close the glossary
   */
  closeGlossary: PropTypes.func.isRequired,
};

MainComponent.defaultProps = {};

const GlossaryDispatch = React.createContext(null);

/**
 * Provider:
 *  - Component wraps its childern with  Glossary-State-Provider.
 */
export function Provider(props) {
  return (
    <GlossaryDispatch.Provider
      value={props.hook}>
      {props.children}
    </GlossaryDispatch.Provider>
  );
}

const ProviderSharedPropTypes = {
  /**
   * String value searched in glossary
   */
  searchText: PropTypes.string.isRequired,
  /**
   * List of categories to be displayed for the search Glossary Items
   */
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
    })
  ).isRequired,
  /**
   * Async function to fetch glossary list
   */
  fetchGlossaryList: PropTypes.func.isRequired,
  /**
   * Async function to fetch glossary Item content
   */
  fetchGlossaryItem: PropTypes.func.isRequired,
};

Provider.propTypes = {
  ...ProviderSharedPropTypes,
  /**
   *  Main Glossary components that needs glossary state.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
};

Provider.defaultProps = {
  searchText: '',
  categories: [],
};

/**
 * Glossary:
 *  A Glossary lising for major components
 */
function Glossary(props) {
  return (
    <Provider
      hook={props.hook}
      searchText={props.searchText}
      categories={props.categories}
      fetchGlossaryItem={props.fetchGlossaryItem}
      fetchGlossaryList={props.fetchGlossaryList}>
      <MainComponent closeGlossary={props.closeGlossary} />
    </Provider>
  );
}

Glossary.propTypes = {
  ...MainComponent.propTypes,
  ...ProviderSharedPropTypes,
};

Glossary.defaultProps = Provider.defaultProps;

export {Glossary, GlossaryDispatch};
